import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useGlobalFeatureFlags,
  useUpdateFeatureFlag,
} from "@/hooks/api";
import { type FeatureFlag } from "@/lib/api_client";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/cn";

/** Human-friendly labels for known flag keys (falls back to the raw key). */
const FLAG_LABELS: Record<string, string> = {
  over_18_limit: "18-årsgrense",
};

function Toggle({
  checked,
  onChange,
  disabled,
  ariaLabel,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
        checked ? "bg-admin-primary" : "bg-admin-slate"
      )}
    >
      <span
        className={cn(
          "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
          checked ? "translate-x-5" : "translate-x-0.5"
        )}
      />
    </button>
  );
}

export function FeatureFlagsPage() {
  const { data: flags, isLoading, isError } = useGlobalFeatureFlags();
  const updateFlag = useUpdateFeatureFlag();

  const handleToggle = (flag: FeatureFlag) => {
    updateFlag.mutate(
      { id: flag.id, enabled: !flag.enabled },
      {
        onSuccess: (updated) =>
          toast.success(
            `${FLAG_LABELS[updated.key] ?? updated.key} ${
              updated.enabled ? "aktivert" : "deaktivert"
            }`
          ),
        onError: () => toast.error("Kunne ikke oppdatere feature flag"),
      }
    );
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold">Feature flags</h2>
        <p className="text-sm text-admin-text-secondary mt-1">
          Slå funksjoner av og på for hele plattformen.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
        </div>
      )}

      {isError && (
        <p className="text-admin-negative">
          Kunne ikke laste feature flags.
        </p>
      )}

      <div className="space-y-3">
        {flags?.map((flag) => {
          const pending =
            updateFlag.isPending && updateFlag.variables?.id === flag.id;
          return (
            <Card key={flag.id}>
              <CardContent className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="font-medium text-admin-text-primary">
                    {FLAG_LABELS[flag.key] ?? flag.key}
                  </p>
                  {flag.description && (
                    <p className="text-sm text-admin-text-secondary mt-0.5">
                      {flag.description}
                    </p>
                  )}
                  <p className="text-xs text-admin-text-subtle mt-1 font-mono">
                    {flag.key}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={cn(
                      "text-xs font-medium",
                      flag.enabled
                        ? "text-admin-primary"
                        : "text-admin-text-subtle"
                    )}
                  >
                    {flag.enabled ? "På" : "Av"}
                  </span>
                  <Toggle
                    checked={flag.enabled}
                    disabled={pending}
                    onChange={() => handleToggle(flag)}
                    ariaLabel={FLAG_LABELS[flag.key] ?? flag.key}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}

        {flags && flags.length === 0 && (
          <p className="text-admin-text-subtle">
            Ingen feature flags er definert.
          </p>
        )}
      </div>
    </div>
  );
}
